import DOMPurify, { type Config } from 'dompurify';

// An allowlist, not a denylist: the body carries values clients and experts
// wrote (the request name, the expert's name), and this page is the human check
// before anything is sent, so nothing in it may act, load or cover the page.
// DOMPurify's defaults keep forms, buttons, SVG links, images and the style
// attribute; only the text and table markup of the template is kept here.
// - No href: the links are the recipient's own invite and unsubscribe links.
//   Followed from here they would add the staff member to the request or
//   unsubscribe the expert, so they read as text (the footer still shows).
// - No style: inline styles can position an overlay over the real recipients
//   and buttons, and <style> could restyle the app around the preview.
// Sanitized as the api rendered it, not through safeHtml, which unescapes
// %XX first: the body is text the preparer (or Claude) wrote, so "100%" or
// "%41" must read here exactly as the email will.
const PREVIEW_SANITIZE: Config = {
  ALLOWED_TAGS: [
    'a',
    'b',
    'br',
    'div',
    'em',
    'i',
    'p',
    'span',
    'strong',
    'table',
    'tbody',
    'td',
    'th',
    'thead',
    'tr',
    'u',
  ],
  ALLOWED_ATTR: ['align', 'bgcolor', 'border', 'cellpadding', 'cellspacing', 'valign', 'width'],
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: false,
};

interface EmailPreviewProps {
  subject: string;
  body: string;
  /** The draft changed since this was rendered; a newer render is on its way. */
  updating?: boolean;
}

/** The invitation as the api rendered it for one expert, sanitized. */
export default function EmailPreview({ subject, body, updating }: EmailPreviewProps): JSX.Element {
  return (
    <div
      aria-busy={updating || undefined}
      className={`flex flex-col gap-12 transition-opacity ${updating ? 'opacity-60' : ''}`}
    >
      <p className="m-[0] text-on-surface body-1">
        <span className="text-on-surface-muted">Subject: </span>
        {subject}
      </p>
      {/* Light in dark mode too: the email carries its own light palette, as in a mail client.
          The inert links take the body text colour (the global teal fails contrast on white);
          hover:underline because the global a:hover rule comes later and would drop it. */}
      <article
        aria-label="Email preview"
        className="overflow-x-auto rounded-sm border border-surface-border bg-white p-16 text-gray-900 [&_a:hover]:underline [&_a]:text-current [&_a]:underline"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(body, PREVIEW_SANITIZE) }}
      />
    </div>
  );
}
